import type { SeriesRegistrationAttributes } from '@/server/models'

import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { SQL, type SQLFragment } from '@/client/query'

import CourseQ from './CourseQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryLimitToSQL,
  queryOrderToSQL,
} from './common'
import { SESSION_FEEDBACK_TYPE_TUTOR, type TutorSessionFeedbackRating } from './constants'
import SeriesQ from './SeriesQ'
import SessionAttendeeQ from './SessionAttendeeQ'
import SessionFeedbackQ from './SessionFeedbackQ'
import SessionQ from './SessionQ'
import SessionRegistrationQ from './SessionRegistrationQ'
import UserQ from './UserQ'

const SeriesRegistrationQ = {
  ...makeQueryModelV2<SeriesRegistrationAttributes>('series_registration')([
    'id',
    'user_id',
    'series_id',
    'details',
    'withdrawn_at',
    'snooze_confirmation_until',
    'withdrawal_source',
    'withdrawal_reason',
    'internal_state',
    'created_at',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Series: {
      query: SeriesQ,
      foreignKey: SeriesQ.id,
      sourceKey: SeriesRegistrationQ.series_id,
    },
    User: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: SeriesRegistrationQ.user_id,
    },
  }),

  where: (
    {
      id,
      paranoid = true,
      seriesId,
      userId,
      includeSeries,
      includeCourse,
      includeUser,
      includeNextSession,
      include = [],
      groupBy = null,
      order = null,
      limit = null,
    }: {
      id?: number | null | SQLFragment<number>
      paranoid?: boolean
      seriesId?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      includeSeries?: boolean
      includeCourse?: boolean
      includeUser?: boolean
      includeNextSession?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      limit?: number | null
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<SeriesRegistrationAttributes> => SQL`
            FROM ${SeriesRegistrationQ}
            ${includeAssociationsToSql(SeriesRegistrationQ, include)}
            ${
              includeSeries === true || includeNextSession === true || includeCourse === true
                ? SQL`INNER JOIN ${SeriesQ} ON ${SeriesQ.id} = ${SeriesRegistrationQ.series_id}`
                : SQL``
            }
            ${
              includeCourse === true
                ? SQL`INNER JOIN ${CourseQ} ON ${CourseQ.id} = ${SeriesQ.course_id}`
                : SQL``
            }
            ${
              includeUser === true
                ? SQL`INNER JOIN ${UserQ} ON ${UserQ.id} = ${SeriesRegistrationQ.user_id}`
                : SQL``
            }
            ${
              includeNextSession === true
                ? SQL`INNER JOIN ${SessionQ} ON ${SessionQ.id} = (
                        SELECT ${SessionQ.id} ${SeriesQ.getNextSession()})`
                : SQL``
            }
            WHERE
                ${id != null ? SQL`${SeriesRegistrationQ.id} = ${id} AND` : SQL``}
                ${
                  seriesId != null ? SQL`${SeriesRegistrationQ.series_id} = ${seriesId} AND` : SQL``
                }
                ${userId != null ? SQL`${SeriesRegistrationQ.user_id} = ${userId} AND` : SQL``}
                ${paranoid ? SQL`${SeriesRegistrationQ.deleted_at} IS NULL AND` : SQL``}
                ${customWhere != null ? customWhere : SQL`TRUE`}
            ${queryGroupByToSQL(groupBy)}
            ${queryOrderToSQL(order)}
            ${queryLimitToSQL(limit)}
        `,

  isWithdrawn: (): SQLFragment<boolean> => SQL`
        (${SeriesRegistrationQ.withdrawn_at} IS NOT NULL AND ${SeriesRegistrationQ.withdrawn_at} < NOW())
    `,

  countRatings: (rating: TutorSessionFeedbackRating): SQLFragment<number> =>
    SQL`COUNT(*) ${SessionFeedbackQ.where({
      rating,
      userId: SeriesRegistrationQ.user_id,
      type: SESSION_FEEDBACK_TYPE_TUTOR,
      include: [
        {
          association: 'Session',
          required: true,
          where: SQL`${SessionQ.series_id} = ${SeriesRegistrationQ.series_id}`,
        },
      ],
    })}`,

  getActiveSeriesRegistrations: ({
    worldSlug,
    userId,
  }: {
    worldSlug?: string | null
    userId: number
  }): SQLFragment<SeriesRegistrationAttributes> =>
    SeriesRegistrationQ.where({
      userId,
      include: [
        {
          association: 'Series',
          where: SeriesQ.hasUpcomingSession(),
          include: [
            {
              association: 'World',
              required: true,
              where: worldSlug != null ? SQL`${WorldQ.slug} = ${worldSlug}` : SQL`TRUE`,
            },
          ],
        },
      ],
    }),

  countSessionsAttended: (): SQLFragment<number> => SQL`
    COUNT(DISTINCT ${SessionAttendeeQ.session_id})
    ${SessionAttendeeQ.where({
      userId: SeriesRegistrationQ.user_id,
      include: [
        {
          association: 'Session',
          required: true,
          where: SQL`${SessionQ.series_id} = ${SeriesRegistrationQ.series_id}`,
        },
      ],
    })}`,

  getMostRecentRegistration: ({
    seriesId,
    userId,
  }: {
    seriesId: number | SQLFragment<number>
    userId: number | SQLFragment<number>
  }): SQLFragment<SeriesRegistrationAttributes> => SQL`
        ${SeriesRegistrationQ.where({
          seriesId,
          userId,
          order: [[SeriesRegistrationQ.created_at, 'desc']],
          paranoid: false,
        })} LIMIT 1
    `,

  getSessionsWithRegistrations: (): SQLFragment<SeriesRegistrationAttributes> => SQL`
      FROM ${SessionQ} LEFT JOIN ${SessionRegistrationQ}
        ON (TRUE
          AND ${SessionRegistrationQ.user_id} = ${SeriesRegistrationQ.user_id}
          AND ${SessionRegistrationQ.session_id} = ${SessionQ.id}
          AND ${SessionRegistrationQ.session_breakout_id} IS NULL
        )
      WHERE ${SessionQ.series_id} = ${SeriesRegistrationQ.series_id}
        AND ${SessionQ.deleted_at} IS NULL
        AND ${SessionRegistrationQ.deleted_at} IS NULL
        AND ${SessionQ.start_date} < NOW()
      ORDER BY ${SessionQ.start_date} ASC
  `,

  hasCompletedPreSeriesSurvey: (): SQLFragment<boolean> =>
    SQL`${SeriesRegistrationQ.internal_state}->'pre_survey_completed_at' IS NOT NULL`,

  hasCompletedPostSeriesSurvey: (): SQLFragment<boolean> =>
    SQL`${SeriesRegistrationQ.internal_state}->'post_survey_completed_at' IS NOT NULL`,

  getTutorsAmongRegistrants: ({
    seriesId,
    worldId,
  }: {
    seriesId: number | null | SQLFragment<number>
    worldId: number | null | SQLFragment<number>
  }): SQLFragment<SeriesRegistrationAttributes> =>
    SeriesRegistrationQ.where({
      seriesId,
      include: [
        {
          association: 'User',
          required: true,
          include: [
            {
              association: 'WorldMember',
              required: true,
              where: SQL`${WorldMemberQ.world_id} = ${worldId} AND ${WorldMemberQ.isAtLeastLevel('tutor')}`,
            },
          ],
        },
      ],
    }),
}

export default SeriesRegistrationQ
